import _ from "lodash";
import {
  DATA_GRID_CELL_TYPES,
  DATA_GRID_CHECKBOX_STATES,
  DEFAULT_STANDARDS_DATA_GRID_PROPERTIES,
  EMPTY_CELL,
} from "./constants";
import {
  getTextCellProps as _getTextCellProps,
  getMetadata as _getMetadata,
  getRowCollapseCellProps as _getRowCollapseCellProps,
  getLabelCellProps as _getLabelCellProps,
  getDropdownCellProps as _getDropdownCellProps,
  getTagCellProps as _getTagCellProps,
  getNumberCellProps as _getNumberCellProps,
  getIconCellProps as _getIconCellProps,
  getIconPickerCellProps as _getIconPickerCellProps,
  getCheckboxCellProps as _getCheckboxCellProps,
  getCellStyles as _getCellStyles,
} from "./cellUtils";

export const getUpdatedProperties = ({ properties, t }) => {
  const defaultProperties = DEFAULT_STANDARDS_DATA_GRID_PROPERTIES({
    t,
  });

  return _.merge({}, defaultProperties, properties);
};

export const convertEdgesToNodeMap = params => {
  const {
    edges,
    columns,
    properties,
    cellUtils,
    actionHandlerFn: _actionHandlerFn,
  } = params;
  const {
    getTextCellProps = _getTextCellProps,
    getMetadata = _getMetadata,
    getRowCollapseCellProps = _getRowCollapseCellProps,
    getLabelCellProps = _getLabelCellProps,
    getDropdownCellProps = _getDropdownCellProps,
    getTagCellProps = _getTagCellProps,
    getNumberCellProps = _getNumberCellProps,
    getIconCellProps = _getIconCellProps,
    getIconPickerCellProps = _getIconPickerCellProps,
    getCheckboxCellProps = _getCheckboxCellProps,
    getIsCellDisabled = () => false,
    getCellStyles = _getCellStyles,
    postProcessingFn = () => {},
  } = cellUtils || {};

  const sideNavData = [];
  // if there are more than 1 rows with depth>1 then we can show the outline
  const depthMap = {};

  const columnsLength = _.size(columns);
  const nodeMap = new Map();
  // It will have the map for id: originalNode since it's required for caching
  const originalNodeMap = new Map();

  _.forEach(edges, (edge, index) => {
    const node = edge?.node || edge || {};

    const {
      id,
      depth,
      uid,
      spanAllCols,
      label,
      defaultLanguageLabel,
      allChildren,
      children,
    } = node || {};
    const parentId =
      node?.parent || node?.parentId || node?.parentData?.id || null;

    const currentNode = {
      rowId: id,
      columns: [],
      children: [],
      depth,
      uid,
      allChildren: allChildren || children,
      parentId,
      metadata: getMetadata({ node, nodeMap, parentId, index }),
    };

    if (depth === 1) {
      sideNavData.push({
        id,
        label: label || defaultLanguageLabel,
      });
    }
    depthMap[depth] = (depthMap[depth] || 0) + 1;

    currentNode.columns = spanAllCols
      ? _.times(columnsLength, () => ({
          cellType: DATA_GRID_CELL_TYPES.TEXT,
          value: label,
          colSpan: [0, columnsLength - 1],
          styles: {
            backgroundColor: "other",
          },
        }))
      : _.map(columns, columnId => {
          const columnProperties = properties[columnId] || {};
          const { cellConfig: _cellConfig, customConfig } =
            columnProperties || {};

          // Copy the cellConfig to avoid mutating the original object
          const cellConfig = { ..._cellConfig };

          const { cellType } = cellConfig || {};
          const { actionProp, actionType } = customConfig || {};

          const actionHandlerFn = (params, type) => {
            _actionHandlerFn({
              type: type ?? actionType,
              details: {
                params,
                nodeMap,
                properties,
              },
            });
          };

          if (actionProp && actionType) {
            cellConfig[actionProp] = actionHandlerFn;
          }

          cellConfig.isDisabled = getIsCellDisabled({
            columnId,
            currentNode,
          });

          cellConfig.styles = getCellStyles({
            node,
            cellConfig,
            customConfig,
            columnId,
            currentNode,
            properties,
          });

          switch (cellType) {
            case DATA_GRID_CELL_TYPES.TEXT:
              return getTextCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
              });

            case DATA_GRID_CELL_TYPES.ROW_COLLAPSE:
              return getRowCollapseCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
              });

            case DATA_GRID_CELL_TYPES.LABEL:
              return getLabelCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
                actionHandlerFn,
              });

            case DATA_GRID_CELL_TYPES.DROPDOWN:
              return getDropdownCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
              });

            case DATA_GRID_CELL_TYPES.TAG:
              return getTagCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
              });

            case DATA_GRID_CELL_TYPES.NUMBER:
              return getNumberCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
              });

            case DATA_GRID_CELL_TYPES.ICON:
              return getIconCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
                properties,
                actionHandlerFn,
              });

            case DATA_GRID_CELL_TYPES.ICON_PICKER:
              return getIconPickerCellProps({
                node,
                cellConfig,
                customConfig,
              });

            case DATA_GRID_CELL_TYPES.CHECKBOX:
              return getCheckboxCellProps({
                node,
                cellConfig,
                customConfig,
                columnId,
                currentNode,
              });

            default:
              return EMPTY_CELL;
          }
        });

    nodeMap.set(id, currentNode);
    originalNodeMap.set(id, node);

    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId).children.push(id);
    }
    postProcessingFn({ currentNode, nodeMap });
  });

  // check if more than 1 nodes have depth > 1 and more than 1 node with depth==1
  if (!((depthMap[1] || 0) > 1 && (depthMap[2] || 0) > 1)) {
    sideNavData.length = 0;
  }

  return { nodeMap, sideNavData, originalNodeMap };
};

export const handleSelectionChange = ({
  params = [],
  selectedRows,
  setSelectedRows,
}) => {
  const rows = _.map(params, ({ cellCoods, newValue }) => ({
    rowId: cellCoods.rowId,
    newValue,
  }));

  const selected = [];
  const removed = [];
  const newSelectedRows = new Set(selectedRows);

  _.forEach(rows, row => {
    const { rowId, newValue } = row;
    const isChecked = newValue?.value === DATA_GRID_CHECKBOX_STATES.CHECKED;
    const rowsToProcess = [rowId];

    if (isChecked) {
      selected.push(...rowsToProcess);
    } else {
      removed.push(...rowsToProcess);
    }
  });

  if (_.size(selected) > 0 || _.size(removed) > 0) {
    removed.forEach(rowId => newSelectedRows.delete(rowId));
    selected.forEach(rowId => newSelectedRows.add(rowId));
    setSelectedRows(newSelectedRows);
  }
  return {
    addedNodes: selected,
    removedNodes: removed,
    selectedNodes: newSelectedRows,
  };
};
